import React, { useEffect } from "react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

import { faArrowDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ChatErrorBanner from "./ChatErrorBanner";
import ChatMessage from "./ChatMessage";
import { CompetencyImportWatcher } from "./CompetencyImportWatcher";

import { useCompetencyDocumentUpload } from "../hooks/useCompetencyDocumentUpload";

import { isCompetencyReimportBlocked } from "../utils/is-competency-reimport-blocked";
import { isUploadInFlight } from "../utils/is-upload-in-flight";

import { useAskPerformYardStore } from "../AskPerformYardStore";

/** Registers the scrollToBottom function in the store so other components can use it. */
const ScrollToBottomSync: React.FC = () => {
	const { scrollToBottom } = useStickToBottomContext();
	const setScrollToBottom = useAskPerformYardStore((state) => state.setScrollToBottom);

	useEffect(() => {
		setScrollToBottom(() => scrollToBottom());
		return () => setScrollToBottom(null);
	}, [scrollToBottom, setScrollToBottom]);

	return null;
};

/** Scroll-to-bottom button that appears when user has scrolled up. */
const ScrollToBottomButton: React.FC<{ hasError: boolean }> = ({ hasError }) => {
	const { isAtBottom, scrollToBottom } = useStickToBottomContext();

	if (isAtBottom) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={() => scrollToBottom()}
			className={`absolute left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-neutral-700 bg-white transition-all hover:bg-neutral-50 ${hasError ? "bottom-14" : "bottom-4"}`}
			aria-label="Scroll to bottom"
		>
			<FontAwesomeIcon icon={faArrowDown} className="h-3 w-3 text-neutral-700" />
		</button>
	);
};

export const ChatMessages: React.FC = () => {
	const messages = useAskPerformYardStore((state) => state.messages);
	const error = useAskPerformYardStore((state) => state.error);
	const status = useAskPerformYardStore((state) => state.status);
	const { fileInputRef, triggerUpload, handleFileSelected } = useCompetencyDocumentUpload();

	const hasError = !!error;
	const uploadDisabled = status === "thinking" || status === "streaming" || isUploadInFlight(messages);
	const competencyFramework = useAskPerformYardStore((state) => state.competencyFramework);
	const uploadBlocked = isCompetencyReimportBlocked(competencyFramework);

	return (
		<StickToBottom className="relative flex-1 overflow-hidden" resize="smooth" initial="smooth">
			<StickToBottom.Content
				scrollClassName="![scrollbar-gutter:auto]"
				className={`flex flex-col items-start gap-2 p-2 pr-4 pl-3 ${hasError ? "pb-12" : ""}`}
			>
				<input
					type="file"
					ref={fileInputRef}
					accept="application/pdf"
					onChange={(event) => {
						const file = event.target.files?.[0];
						event.target.value = "";
						if (file) void handleFileSelected(file);
					}}
					className="hidden"
				/>
				{messages.map((message) => (
					<ChatMessage
						key={message.id}
						message={message}
						onCompetencyUpload={uploadBlocked ? undefined : triggerUpload}
						competencyUploadDisabled={uploadDisabled}
					/>
				))}
			</StickToBottom.Content>

			{error && (
				<div className="absolute right-0 bottom-0 left-0 z-10 px-4 pb-2">
					<ChatErrorBanner type="error" message={error} />
				</div>
			)}

			{messages
				.flatMap((message) => {
					const attachment = message.attachment;
					return attachment?.importTaskId && attachment.narrationMessageId && !attachment.importComplete
						? [
								{
									id: message.id,
									importTaskId: attachment.importTaskId,
									narrationMessageId: attachment.narrationMessageId,
								},
							]
						: [];
				})
				.map(({ id, importTaskId, narrationMessageId }) => (
					<CompetencyImportWatcher
						key={importTaskId}
						taskId={importTaskId}
						attachmentMessageId={id}
						narrationMessageId={narrationMessageId}
					/>
				))}

			<ScrollToBottomSync />
			<ScrollToBottomButton hasError={hasError} />
		</StickToBottom>
	);
};

export default ChatMessages;
